import { createRouter, createWebHistory, RouteRecordRaw, RouteMeta } from 'vue-router';
import LoginView from '@/views/LoginView.vue';
import HomeView from '@/views/HomeView.vue';
import SaleOrdersView from '@/views/SaleOrdersView.vue';
import SaleOrderDetailsView from '@/views/SaleOrderDetailsView.vue';
import PageNotFound from '@/views/PageNotFound.vue';
import ResetTokenExpiredView from '@/views/Auth/ResetTokenExpiredView.vue';
import PriceListsView from '@/views/PriceListsView.vue';
import CopperPriceCalculator from '@/views/CopperPrice/CopperPriceCalculator.vue';
import config from '@/config';
import { authGuard } from '@auth0/auth0-vue';

interface CustomRouteMeta extends RouteMeta {
  title?: string;
}

async function isAuthenticated(to: any, from: any, next: any) {
  let isAuthGuarded = false;
  isAuthGuarded = await authGuard(to);
  if (isAuthGuarded) return next();
  else return next({ name: 'login' });
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    redirect: '/home',
    meta: { title: 'Home | Pricing Engine' } as CustomRouteMeta,
    beforeEnter: isAuthenticated,
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: { title: 'Login | Pricing Engine' } as CustomRouteMeta,
  },

  {
    path: '/auth/reset-token-expired',
    name: 'reset-token-expired',
    component: ResetTokenExpiredView,
    meta: { title: 'Reset Token Expired | Pricing Engine' } as CustomRouteMeta,
  },
  {
    path: '/home',
    name: 'home',
    redirect: '/home/sale-orders',
    components: {
      default: HomeView,
      contentArea: HomeView,
    },
    meta: { title: 'Home | Pricing Engine' } as CustomRouteMeta,
    beforeEnter: isAuthenticated,
    children: [
      // The legacy History and Simulator views were removed in 93e78b3 and
      // Search does the work of both now, but people still have the old
      // paths bookmarked. Send them on rather than show them the 404.
      {
        path: 'history',
        redirect: { name: 'search' },
      },
      {
        path: 'simulator',
        redirect: { name: 'search' },
      },
      {
        path: 'search',
        name: 'search',
        components: {
          default: () => import('@/components/views/search/SearchView.vue'),
          contentArea: () => import('@/components/views/search/SearchView.vue'),
        },
        meta: { title: 'History Search | Pricing Engine' } as CustomRouteMeta,
      },
      {
        path: 'sale-orders',
        name: 'sale-orders',
        components: {
          default: SaleOrdersView,
          contentArea: SaleOrdersView,
        },
        meta: { title: 'Sale Orders | Pricing Engine' } as CustomRouteMeta,
      },
      {
        path: 'sale-orders/:saleOrderId',
        name: 'sale-order',
        components: {
          default: SaleOrderDetailsView,
          contentArea: SaleOrderDetailsView,
        },
        meta: { title: 'Sale Order | Pricing Engine' } as CustomRouteMeta,
      },
      // {
      //   path: 'new-sale-orders/:saleOrderId',
      //   name: 'new-sale-order',
      //   components: {
      //     default: () => import('@/views/NewSaleOrderDetailsView.vue'),
      //     contentArea: () => import('@/views/NewSaleOrderDetailsView.vue'),
      //   },
      //   meta: { title: 'Sale Order | Pricing Engine' } as CustomRouteMeta,
      // },
      {
        path: 'price-lists',
        name: 'price-lists',
        components: {
          default: PriceListsView,
          contentArea: PriceListsView,
        },
        meta: { title: 'Price Lists | Pricing Engine' } as CustomRouteMeta,
      },
      {
        path: 'price-lists/new',
        name: 'price-list-new',
        components: {
          default: () => import('@/views/PriceListForm/PriceListForm.vue'),
          contentArea: () => import('@/views/PriceListForm/PriceListForm.vue'),
        },
        meta: { title: 'New Price List | Pricing Engine' } as CustomRouteMeta,
      },

      {
        path: 'price-lists/:id',
        name: 'price-list-edit',
        components: {
          default: () => import('@/views/PriceListForm/PriceListForm.vue'),
          contentArea: () => import('@/views/PriceListForm/PriceListForm.vue'),
        },
        meta: { title: 'Edit Price List | Pricing Engine' } as CustomRouteMeta,
      },

      {
        path: 'copper-price-calculator',
        name: 'copper-price-calculator',
        components: {
          default: CopperPriceCalculator,
          contentArea: CopperPriceCalculator,
        },
        meta: { title: 'Copper Price Calculator | Pricing Engine' } as CustomRouteMeta,
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    component: PageNotFound,
    meta: { title: 'Page Not Found | Pricing Engine' } as CustomRouteMeta,
  },
];

const router = createRouter({
  history: createWebHistory(config.baseUrls.client),
  routes,
});

router.beforeEach((to, from, next) => {
  const meta = to.meta as CustomRouteMeta;
  if (meta.title) {
    document.title = meta.title;
  } else {
    document.title = 'Pricing Engine';
  }
  next();
});

export default router;
